import { createTheme } from '@mui/material/styles';
import { SCHEMES } from './styles/palette';

// ============================================================================
// DWLF Design System — "Signal from Noise"
// ============================================================================
// Typography: Space Grotesk (headings), IBM Plex Mono (body), Space Mono (UI)
// Colors: Cyan (#00e5ff) primary, near-black (#0a0a0c) background
// Shapes: Sharp edges (no border-radius), brutalist aesthetic
// ============================================================================

// Two colour schemes, Signal dark + Signal light (DWLF-298), both built from styles/palette.js — the
// one place the colours live. MUI keeps the user's choice ('system' | 'light' | 'dark') in localStorage
// and sets <html data-dwlf-theme="…">, which also switches the var(--dwlf-*) tokens. MUI derives the
// light/dark shades of each colour from `main`.
const muiPalette = (s, ink) => ({
  palette: {
    primary: { main: s.accent, contrastText: s['on-accent'] },
    secondary: { main: s.text, contrastText: s.bg },
    background: { default: s.bg, paper: s.surface, surface: s.elevated },
    text: { primary: s.text, secondary: `rgba(${ink}, ${0.5 * s.boost})`, disabled: `rgba(${ink}, ${0.25 * s.boost})` },
    error: { main: s.down },
    warning: { main: s.warn },
    success: { main: s.up },
    info: { main: s.accent },
    divider: `rgba(${ink}, 0.08)`,
  },
});
const signalDark = muiPalette(SCHEMES.dark, '255, 255, 255');
const signalLight = muiPalette(SCHEMES.light, '10, 10, 12');

const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-dwlf-theme' },
  colorSchemes: { dark: signalDark, light: signalLight },
  defaultColorScheme: 'dark',
  typography: {
    // Primary heading font
    fontFamily: '"Space Grotesk", "IBM Plex Mono", monospace',
    h1: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      fontSize: '3rem',
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 500,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 500,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    body1: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.9375rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
    },
    body2: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.8125rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    subtitle2: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    caption: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    overline: {
      fontFamily: '"Space Mono", monospace',
      fontSize: '0.7rem',
      fontWeight: 500,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: '"Space Mono", monospace',
      fontWeight: 500,
      fontSize: '0.8rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 0, // Sharp edges everywhere
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.3)',
    '0 2px 4px rgba(0, 0, 0, 0.3)',
    '0 4px 8px rgba(0, 0, 0, 0.3)',
    '0 6px 12px rgba(0, 0, 0, 0.3)',
    '0 8px 16px rgba(0, 0, 0, 0.3)',
    '0 10px 20px rgba(0, 0, 0, 0.3)',
    '0 12px 24px rgba(0, 0, 0, 0.3)',
    '0 14px 28px rgba(0, 0, 0, 0.3)',
    '0 16px 32px rgba(0, 0, 0, 0.3)',
    ...Array(15).fill('0 16px 32px rgba(0, 0, 0, 0.3)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Fonts loaded via public/index.html link tags
        body: {
          backgroundColor: 'var(--dwlf-bg)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--dwlf-ink-12) var(--dwlf-bg)',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'var(--dwlf-bg)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--dwlf-ink-12)',
            '&:hover': {
              background: 'var(--dwlf-ink-20)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-surface)',
          backgroundImage: 'none',
          border: '1px solid var(--dwlf-ink-06)',
          borderRadius: 0,
          transition: 'border-color 0.2s ease',
          '&:hover': {
            borderColor: 'var(--dwlf-ink-12)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-surface)',
          backgroundImage: 'none',
          border: '1px solid var(--dwlf-ink-06)',
          borderRadius: 0,
          transition: 'border-color 0.2s ease',
          '&:hover': {
            borderColor: 'var(--dwlf-accent)',
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Space Mono", monospace',
          fontWeight: 500,
          fontSize: '0.8rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          borderRadius: 0,
          padding: '12px 24px',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'none',
          },
        },
        contained: {
          backgroundColor: 'var(--dwlf-accent)',
          color: 'var(--dwlf-bg)',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'var(--dwlf-text)',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: 'var(--dwlf-ink-20)',
          borderWidth: '1px',
          color: 'var(--dwlf-ink-60)',
          '&:hover': {
            borderColor: 'var(--dwlf-accent)',
            borderWidth: '1px',
            color: 'var(--dwlf-accent)',
            backgroundColor: 'transparent',
          },
        },
        text: {
          color: 'var(--dwlf-ink-60)',
          '&:hover': {
            backgroundColor: 'var(--dwlf-ink-05)',
            color: 'var(--dwlf-accent)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'var(--dwlf-ink-05)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.7rem',
          fontWeight: 500,
          letterSpacing: '0.05em',
          borderRadius: 0,
          height: '24px',
        },
        colorSuccess: {
          backgroundColor: 'var(--dwlf-up)',
          color: 'var(--dwlf-bg)',
        },
        colorError: {
          backgroundColor: 'var(--dwlf-down)',
          color: 'var(--dwlf-bg)',
        },
        colorPrimary: {
          backgroundColor: 'var(--dwlf-accent)',
          color: 'var(--dwlf-bg)',
        },
        colorWarning: {
          backgroundColor: 'var(--dwlf-warn)',
          color: 'var(--dwlf-bg)',
        },
        outlined: {
          borderColor: 'var(--dwlf-ink-20)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: '1px solid var(--dwlf-ink-06)',
          backgroundColor: 'var(--dwlf-surface)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-elevated)',
          '& .MuiTableCell-head': {
            fontFamily: '"Space Mono", monospace',
            fontWeight: 500,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: 'var(--dwlf-ink-50)',
            borderBottom: '1px solid var(--dwlf-ink-08)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--dwlf-accent-03)',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"IBM Plex Mono", monospace',
          borderColor: 'var(--dwlf-ink-06)',
          padding: '14px 16px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontFamily: '"IBM Plex Mono", monospace',
            borderRadius: 0,
            backgroundColor: 'var(--dwlf-elevated)',
            '& fieldset': {
              borderColor: 'var(--dwlf-ink-10)',
            },
            '&:hover fieldset': {
              borderColor: 'var(--dwlf-ink-20)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'var(--dwlf-accent)',
              borderWidth: '1px',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.85rem',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: '"IBM Plex Mono", monospace',
          borderRadius: 0,
          backgroundColor: 'var(--dwlf-elevated)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-bg)',
          backgroundImage: 'none',
          borderBottom: '1px solid var(--dwlf-ink-06)',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--dwlf-surface)',
          borderLeft: '1px solid var(--dwlf-ink-06)',
          borderRadius: 0,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--dwlf-surface)',
          border: '1px solid var(--dwlf-ink-08)',
          borderRadius: 0,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.85rem',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: 'var(--dwlf-accent-08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--dwlf-accent-12)',
            '&:hover': {
              backgroundColor: 'var(--dwlf-accent-16)',
            },
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&:hover': {
            backgroundColor: 'var(--dwlf-accent-05)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&:hover': {
            backgroundColor: 'var(--dwlf-accent-05)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--dwlf-accent-10)',
            '&:hover': {
              backgroundColor: 'var(--dwlf-accent-12)',
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'var(--dwlf-ink-06)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '24px',
          paddingRight: '24px',
          '@media (min-width: 600px)': {
            paddingLeft: '32px',
            paddingRight: '32px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--dwlf-surface)',
          border: '1px solid var(--dwlf-ink-08)',
          borderRadius: 0,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontFamily: '"IBM Plex Mono", monospace',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: '2px',
          backgroundColor: 'var(--dwlf-accent)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Space Mono", monospace',
          fontWeight: 500,
          fontSize: '0.8rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          minHeight: '48px',
          '&.Mui-selected': {
            color: 'var(--dwlf-accent)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.75rem',
          backgroundColor: 'var(--dwlf-elevated)',
          color: 'var(--dwlf-text)',
          border: '1px solid var(--dwlf-ink-10)',
          borderRadius: 0,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-accent-10)',
          borderRadius: 0,
          height: '2px',
        },
        bar: {
          backgroundColor: 'var(--dwlf-accent)',
          borderRadius: 0,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: 'var(--dwlf-accent)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: '"Space Mono", monospace',
          fontWeight: 500,
          borderRadius: 0,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-surface)',
          border: '1px solid var(--dwlf-ink-06)',
          borderRadius: 0,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontFamily: '"Space Grotesk", sans-serif',
          fontWeight: 500,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            borderRadius: 0,
          },
          '& .MuiSwitch-thumb': {
            borderRadius: 0,
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: 'var(--dwlf-accent)',
          '& .MuiSlider-thumb': {
            borderRadius: 0,
          },
          '& .MuiSlider-track': {
            borderRadius: 0,
          },
          '& .MuiSlider-rail': {
            borderRadius: 0,
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--dwlf-ink-05)',
          borderRadius: 0,
        },
      },
    },
  },
});

export default theme;
